import React, { ReactNode, useEffect, useRef, useMemo, useState } from 'react';
import { useParams, useRouter, useSearchParams} from 'next/navigation';
import {
  Configure,
  InfiniteHits,
  useClearRefinements,
  useCurrentRefinements,
  useHits,
  usePagination,
  useSortBy,
  useRefinementList,
  useInfiniteHits,
  useInstantSearch,
} from 'react-instantsearch-hooks-web';
import { mapProduct } from 'helpers/algolia/map-product';
import { clientRetrieveAttributes } from 'helpers/algolia/client-retrieve-attributes';
import { useFormat } from 'helpers/hooks/useFormat';
import { trackEvent } from 'helpers/customEvents';
import CurrentRefinements from './components/current-refinements';
import DesktopFacets from './components/desktop-facets';
import MobileFacets from './components/mobile-facets';
import SearchHeader from './components/search-header';
import { FacetConfiguration } from './types';
import ProductTile from '../product-tile';
import DesktopSortFacets from './components/desktop-facets/sort-facets';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop } from 'helpers/utils/screensizes';
import { useLocalizedIndex } from 'providers/algolia/localized-index';
import ViewToggle from '../view-toggle';
import ScrollToTopButton from 'components/commercetools-ui/atoms/scroll-to-top';
interface Props {
  noContentSuggestions?: any;
  slug?: string;
  searchQuery?: string;
  facetsConfiguration: Record<string, FacetConfiguration>;
  suggestions?: any;
}

/** Heading of /search when there is no `?query=` (the page then lists the whole search index). */
const SEARCH_PAGE_TITLE = 'Products in Search';
interface NoResultProps {
  fallback: ReactNode;
}
function NoResultsBoundary({ children, fallback }: React.PropsWithChildren<NoResultProps>) {
  const { results } = useHits();
  if (results && !results?.__isArtificial && results?.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    );
  }
  return children;
}
function NoResults({ results, suggestions, searchQuery }: any) {
  const { formatMessage } = useFormat({ name: 'common' });
  const router = useRouter();

  const handleSuggestion = (suggestion: string) => {
    router.push(`/search?query=${encodeURIComponent(suggestion)}`);
  };
  return (
    <div>
      {searchQuery && results?.nbHits === 0 ? (
        <div className="m-auto max-w-[900px] text-center lg:!text-left">
          <p className={`results-zero no-result-container mb-2`}>No products found</p>
          <div className="d-inline">
            {formatMessage({
              id: 'no.resultfound.query',
              defaultMessage: 'No search results are found for your query:',
            })}
            <span className="font-bold"> &quot;{searchQuery ? searchQuery : ''}&quot;</span>.
            {suggestions.length > 0 && (
              <p className="d-inline">
                {' '}
                {formatMessage({
                  id: 'did.you.mean',
                  defaultMessage: 'Did you mean?',
                })}{' '}
                {suggestions.map((suggestion: any, index: any) => (
                  <>
                    <span
                      role="button"
                      onClick={() => handleSuggestion(suggestion?.query)}
                      key={index}
                      className="text-decoration-underline font-medium"
                    >
                      {suggestion?.query}
                    </span>
                    <span className="text-decoration-none">{index < suggestions.length - 1 && ', '}</span>
                  </>
                ))}
              </p>
            )}
            {suggestions.length === 0 && (
              <p className="d-inline">
                {' '}
                {formatMessage({
                  id: 'search.popular.categories',
                  defaultMessage: 'You might want to search for popular categories',
                })}{' '}
                {['Tapware', 'Sanitaryware', 'Contura', 'Urbane II'].map((cat: any, index: any) => (
                  <>
                    <span
                      role="button"
                      onClick={() => handleSuggestion(cat)}
                      key={index}
                      className="text-decoration-underline font-medium"
                    >
                      {cat}
                    </span>
                    <span className="text-decoration-none">{index < cat.length - 1 && ', '}</span>
                  </>
                ))}
              </p>
            )}
          </div>
        </div>
      ) : (
        <p className={`${results?.nbHits === 0 && 'results-zero'} no-result-container`}>No products found</p>
      )}
    </div>
  );
}
const ProductListAlgolia: React.FC<Props> = ({
  slug,
  searchQuery,
  facetsConfiguration,
  noContentSuggestions,
  suggestions,
}) => {
  const [isTabletSize] = useMediaQuery(desktop);
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const { locale } = useParams();
  const searchParams = useSearchParams();
  const queryParam = searchParams?.get('query') || undefined;
  const isPdp = searchParams?.get('isPdp') || undefined;
  const [loading, setLoading] = useState(true);
  
  // Auto-apply range facet once if the query matches a range value
  const { refine: refineRange } = useRefinementList({ attribute: 'searchableAttributes.range' });
  useEffect(() => {
    setLoading(true);
    if (queryParam && isPdp && isPdp !== 'false') {
      refineRange(queryParam);
    }
  }, [queryParam, isPdp, refineRange]);
  
  const { currentRefinement } = usePagination();
  const { refine: clearAllRefinements } = useClearRefinements();
  const { items: currentRefinements } = useCurrentRefinements();

  const activeColour = useMemo(() => {
    const colourGroup = currentRefinements.find((item) =>
      item.attribute.toLowerCase().includes('colour') || item.attribute.toLowerCase().includes('color')
    );
    const ref = colourGroup?.refinements?.[0];
    return (ref?.value ?? ref?.label) as string | undefined;
  }, [currentRefinements]);

  const { indexName: productsIndex } = useLocalizedIndex({ type: 'products' });
  const { setUiState } = useInstantSearch();
  const [persistedFacetFilters, setPersistedFacetFilters] = useState<string[] | string[][]>([]);
  const hasHydratedRefinements = useRef(false);
  const previousSearchQuery = useRef<string | undefined>(searchQuery);

  const storageKey = useMemo(() => `product_list_facet_filters_${locale}_${slug || 'all'}`, [locale, slug]);
  
  const { showMore, isLastPage } = useInfiniteHits();
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // Registers the sort-by widget: the search index ("New") and its name-sorted variants.
  useSortBy({
    items: [
      {
        label: formatProductMessage({ id: 'new-products', defaultMessage: 'New' }),
        value: productsIndex,
      },
      {
        label: formatProductMessage({ id: 'nameAsc', defaultMessage: 'Name A-Z' }),
        value: productsIndex.replace('-main', '') + '_name_asc',
      },
      {
        label: formatProductMessage({ id: 'nameDsc', defaultMessage: 'Name Z-A' }),
        value: productsIndex.replace('-main', '') + '_name_desc',
      },
    ],
  });
  const { items } = useCurrentRefinements();
  const { results } = useHits();

  const [currentView, setCurrentView] = useState<'grid' | 'list'>('grid');  
  const handleViewChange = (view: 'grid' | 'list') => setCurrentView(view);


  useEffect(() => {
    setLoading(true);
    if (previousSearchQuery.current !== searchQuery) {
      // New search query detected - clear all filters
      if (searchQuery && previousSearchQuery.current !== undefined) {
        clearAllRefinements();
        // Clear localStorage for this page when starting a new search
        window.localStorage.removeItem(storageKey);
        setPersistedFacetFilters([]);
        hasHydratedRefinements.current = false;
      }
      previousSearchQuery.current = searchQuery;}
      }, [searchQuery, clearAllRefinements, storageKey]);
useEffect(() => {
    // Only load saved filters if there's no search query
    if (!searchQuery) {
      try {
        const saved = typeof window !== 'undefined' ? window.localStorage.getItem(storageKey) : null;
        if (saved) {
          const parsed = JSON.parse(saved);
          setPersistedFacetFilters(parsed);
        }
      } catch {
        // ignore parsing errors
      }
    }
  }, [storageKey, searchQuery]);

  // Save current filters to localStorage whenever they change (only for non-search pages)
  useEffect(() => {
    try {
      if (!currentRefinements || searchQuery) return; // Don't save filters during search

      const groupedByAttribute = currentRefinements
        .map((facetItem: any) =>
          (facetItem?.refinements || []).map((ref: any) => `${facetItem.attribute}:${ref.value}`)
        )
        .filter((arr: any) => Array.isArray(arr) && arr.length > 0);

      if (groupedByAttribute.length > 0) {
        window.localStorage.setItem(storageKey, JSON.stringify(groupedByAttribute));
      } else {
        window.localStorage.removeItem(storageKey);
      }
    } catch {}
  }, [currentRefinements, storageKey, searchQuery]);

  // Restore saved filters from localStorage (only for non-search pages)
  useEffect(() => {
    if (hasHydratedRefinements.current || searchQuery) return; // Don't restore filters during search

    const saved = persistedFacetFilters as any;
    const hasSaved = Array.isArray(saved) && saved.length > 0;

    if (hasSaved) {
      try {
        const currentTokens = new Set<string>(
          (Array.isArray(currentRefinements) ? currentRefinements : [])
            .flatMap((facetItem: any) =>
              (facetItem?.refinements || []).map((ref: any) => `${facetItem.attribute}:${ref.value}`)
            )
        );

        const groups: string[][] = saved as any;
        const refinementList: Record<string, string[]> = {};

        for (const group of groups) {
          for (const token of group) {
            if (currentTokens.has(token)) continue;
            const [attribute, ...rest] = String(token).split(':');
            const value = rest.join(':');
            if (!attribute || value === undefined) continue;
            if (!refinementList[attribute]) refinementList[attribute] = [];
            if (!refinementList[attribute].includes(value)) refinementList[attribute].push(value);
          }
        }

        if (Object.keys(refinementList).length > 0) {
          setUiState((prev: any) => ({
            ...prev,
            [productsIndex]: {
              ...((prev || {})[productsIndex] || {}),
              refinementList: {
                ...((((prev || {})[productsIndex] || {}).refinementList) || {}),
                ...refinementList,
              },
            },
          }));
        }

        hasHydratedRefinements.current = true;
      } catch {}
    } else {
      hasHydratedRefinements.current = true;
    }
  }, [persistedFacetFilters, currentRefinements, setUiState, productsIndex, searchQuery]);

  // Reset hydration flag when storage key changes (new page/category)
  useEffect(() => {
    hasHydratedRefinements.current = false;
  }, [storageKey]);

  // Set loading to false when results are available
  useEffect(() => {
    if (results) {
      setLoading(false);
    }
  }, [results]);  

  // typing workaround for Configure props in this context
  const ConfigureSearch: any = Configure;

  /**
   * Tile fields ∪ the configured facet attributes. This list is what cachedProductsSearchClient pulls
   * the whole result set with, so it must cover every facet the panel renders — see
   * helpers/algolia/client-retrieve-attributes.ts. Memoised so the InstantSearch params stay
   * referentially stable and don't retrigger the dataset load on every render.
   */
  const retrieveAttributes = useMemo(() => clientRetrieveAttributes(facetsConfiguration), [facetsConfiguration]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLastPage) {
          const pageNumber = typeof currentRefinement === 'number' ? `${currentRefinement + 1}` : 'infinite_scroll';
          const collectionName = slug || 'search';
          trackEvent('pagination_click', {
            page_number: pageNumber,
            collection_name: collectionName,
            type: 'infinite_scroll',
            pageContext: collectionName,
          });
          showMore();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinelRef.current);
    return () => {
      observer.disconnect();
    };
  }, [isLastPage, showMore, currentView]);


  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
        <p className="text-gray-600 text-lg font-medium">
          {searchQuery ? 'Searching products...' : 'Loading products...'}
        </p>
      </div>
    );
  }

  return (
    <>
      <div id="plp-container" className={searchQuery ? 'bg-white' : 'bg-[#F3EDE9]'}>
        <ConfigureSearch
          query={searchQuery ?? ''}
          hitsPerPage={12}
          // No `facets` prop on purpose. Passing one here registers those attributes as CONJUNCTIVE
          // (helper `state.facets`), which lands their counts in `results.facets` — but useDynamicFacets
          // shows a facet only if it appears in `results.disjunctiveFacets`, so the panel went blank.
          // Leaving it off lets each refinementList widget declare its own attribute as disjunctive, so the
          // request carries exactly the refinable facets (5 here) instead of ['*'] (54 on this index).
          attributesToHighlight={[]} // Disable highlighting
          attributesToSnippet={[]} // Disable snippetting
          attributesToRetrieve={retrieveAttributes}
          maxValuesPerFacet={999}
        />
        {searchQuery ? (
          <div className="mt-[1rem] px-24 lg:mt-[0.75rem] lg:px-48 xl:px-60 2xl:mt-[2rem] 2xl:px-80">
            <SearchHeader
              query={searchQuery ?? ''}
              noContentSuggestions={noContentSuggestions}
            />
          </div>
        ) : (
          <>
            <div id="container-scroll" className="flex flex-col items-end">
              <h1 className="w-full pb-32 pt-48 text-center text-32 font-medium leading-8 text-[#090909] md:text-40 2xl:pb-48 2xl:pt-80 2xl:text-48">
                {SEARCH_PAGE_TITLE}
              </h1>
              <div className="px:24 mt-8 flex w-full items-center justify-between gap-48 lg:px-48 xl:px-60 2xl:px-80">
                <div className="lg:ml-[16.5rem]  2xl:ml-[23rem]">
                  <CurrentRefinements />
                </div>
                <div className="hidden lg:flex items-center gap-4">
                  <DesktopSortFacets />
                  <ViewToggle onViewChange={handleViewChange} />
                </div>
              </div>
            </div>
          </>
        )}
        <div
          className={`items-start gap-24 pt-24 md:flex md:flex-row lg:px-48 xl:px-60 2xl:gap-48 2xl:px-80 ${
            searchQuery ? '' : 'px-24 md:px-28'
          } ${results?.nbHits === 0 && items?.length ===0? 'pb-0' : 'pb-48 xl:pb-0 2xl:pb-48'}`}
        >
          <div
            className={
              searchQuery
                ? `${
                    results?.nbHits === 0&& items?.length ===0 ? '' : 'lg:block'
                  } hidden bg-white lg:mt-40  lg:min-w-[15rem]  2xl:min-w-[20rem]`
                : `${
                    results?.nbHits === 0 && items?.length ===0? '' : 'lg:block'
                  }  hidden bg-[#F3EDE9] lg:block lg:min-w-[15rem] 2xl:min-w-[20rem]`
            }
          >
            <DesktopFacets facetsConfiguration={facetsConfiguration} />
          </div>
          <div className={searchQuery ? 'w-full flex-1  bg-white lg:mt-40' : 'w-full bg-[#F3EDE9]'}>
            {searchQuery && (
              <h1 className="px-24 py-12 text-32 font-medium leading-8 text-[#090909] md:py-32  md:px-28 md:text-40 lg:hidden lg:py-0  2xl:text-48">
                Products
              </h1>
            )}
            {searchQuery && (
              <div className=''>
              <div id="product-suggestions" className="pb-10 md:pb-24 lg:pb-0 2xl:pb-0 xl:pb-0 flex items-start" >
                <CurrentRefinements />
                {isTabletSize ? (
                  <NoResultsBoundary fallback={<span></span>}>
                    <div className="ml-auto flex flex-col items-end">
                      <DesktopSortFacets />
                    </div>
                  </NoResultsBoundary>
                ) : (
                  <></>
                )}
                </div>
              </div>
            )}

            <div
              className={`${searchQuery ? 'px-24 md:px-28' : ''} ${
                results?.nbHits === 0 && items?.length === 0 && 'hidden'
              } flex items-center justify-between gap-2 lg:block`}
            >
              <div className="block lg:hidden">
                <div className="cursor-pointer py-4 text-[14px] bg-transparent">
                  <ViewToggle onViewChange={handleViewChange} />
                </div>
              </div>
              <MobileFacets facetsConfiguration={facetsConfiguration} />
            </div>


            {isTabletSize ? (
              <>
                <NoResultsBoundary fallback={<NoResults results={results} suggestions={suggestions} searchQuery={searchQuery} />}>
                  {currentView === 'list' && (
                    <div className="overflow-x-auto">
                      <div className="hidden lg:flex font-semibold text-gray-800 text-[15px]">
                        <div className="px-6 py-4 w-[60%] text-left">Product</div>
                        <div className="px-6 py-4 w-[20%] text-left">Code</div>
                        <div className="px-6 py-4 w-[20%] text-left">RRP</div>
                      </div>
                    </div>
                  )}

                  <InfiniteHits
                    showPrevious={false}
                    hitComponent={({ hit }: any) => (
                      <ProductTile
                        product={mapProduct(hit, locale, searchQuery, activeColour)}
                        layout={currentView}
                        isSearchResult={!!searchQuery}
                        isCategoryPage={true}
                      />
                    )}
                    classNames={{
                      root:
                        currentView === 'grid'
                          ? searchQuery
                            ? 'px-24 md:px-28 pt-32'
                            : 'pt-32 lg:pt-0'
                          : searchQuery
                            ? 'pt-32'
                            : 'pt-32 lg:pt-0',

                      list:
                        currentView === 'grid'
                          ? 'grid grid-cols-1 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-16 [&>div]:aspect-[3/4]'
                          : 'flex flex-col',

                      loadMore: '!hidden',
                    }}
                    translations={{
                      showMoreButtonText: formatProductMessage({
                        id: 'show.more',
                        defaultMessage: 'Show More',
                      }),
                    }}
                  />
                  <div ref={sentinelRef} className="h-10"></div>

                </NoResultsBoundary>
              </>
            ) : (
              <NoResultsBoundary fallback={<NoResults results={results} suggestions={suggestions} searchQuery={searchQuery} />}>
                <InfiniteHits
                  showPrevious={false}
                  hitComponent={({ hit }: any) => (
                    <ProductTile
                      product={mapProduct(hit, locale, searchQuery, activeColour)}
                      layout={currentView}
                      isSearchResult={!!searchQuery}
                      isCategoryPage={true}
                    />
                  )}
                  classNames={{
                    root: searchQuery ? 'px-24 md:px-28 pt-32' : 'pt-32 lg:pt-0',
                    list: 'grid md:grid-cols-2 xl:grid-cols-3 gap-16 ',
                    loadMore: '!hidden',
                  }}
                  translations={{
                    showMoreButtonText: formatProductMessage({ id: 'show.more', defaultMessage: 'Show More' }),
                  }}
                />
                <div ref={sentinelRef} className="h-10"></div>
              </NoResultsBoundary>
            )}
          </div>
        </div>
      </div>
      <ScrollToTopButton />
    </>
  );
};
export default ProductListAlgolia;